import React from "react";
import {
    createTheme as createMuiTheme, CSSObject,
} from "@mui/material";
import {Shadows} from "@mui/material/styles/shadows";
import {CheckboxOff, CheckboxOn, DropdownIcon, LinaArrowRight} from "~/ui/icons";
import {TypographyProps} from "@mui/system";


declare module '@mui/material/styles' {
    interface Palette {
        additionalPrimary: Palette['primary']
        additionalSecondary: Palette['secondary']
        additionalPrimaryDisabled: Palette['primary']
        onAdditionalPrimary: Palette['primary']
    }

    interface PaletteOptions {
        additionalPrimary: PaletteOptions['primary']
        additionalPrimaryDisabled: PaletteOptions['primary']
        additionalSecondary: PaletteOptions['secondary']
        onAdditionalPrimary: PaletteOptions['primary']
    }
}


declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        headerTitle: true
        menuTitle: true
        searchText: true
        listText: true
        statusText: true

        cardMenuTitle: true
        cardMenuStatus: true

        screenTitle: true
        screenText: true
        screenSubtitle: true

        cardTitle1: true
        cardTitle2: true
        cardText: true

        inputTitle1: true
        inputText1: true
        inputTitle2: true
        inputText2: true

        btnText1: true
        btnText2: true

        elementText: true
        tagsText: true

        countText: true
    }

    interface TypographyPropsColorOverrides {
        additionalPrimary: true
        additionalSecondary: true
    }
}

declare module '@mui/material/Button' {
    interface ButtonClasses {
        outlinedAdditionalPrimary: ButtonClasses["outlinedPrimary"]
        outlinedAdditionalSecondary: ButtonClasses["outlinedSecondary"]
        containedAdditionalPrimary: ButtonClasses["containedPrimary"]
        textAdditionalPrimary: ButtonClasses["textPrimary"]
        textAdditionalSecondary: ButtonClasses["textSecondary"]
        textSizeMini: ButtonClasses["textSizeSmall"]
        sizeForty: ButtonClasses["sizeSmall"]
    }

    interface ButtonPropsColorOverrides {
        additionalPrimary: true
        additionalSecondary: true
    }

    interface ButtonPropsSizeOverrides {
        mini: true
        forty: true
    }
}


declare module '@mui/material/IconButton' {
    interface IconButtonClasses {
        sizeMini: IconButtonClasses["sizeSmall"]
        sizeForty: IconButtonClasses["sizeSmall"]
    }
    interface IconButtonPropsSizeOverrides {
        mini: true
        forty: true
    }
}

declare module '@mui/material/SvgIcon' {
    interface SvgIconPropsColorOverrides {
        additionalPrimary: true
        additionalSecondary: true
    }
}


declare module '@mui/material/Chip' {
    interface ChipPropsColorOverrides {
        additionalPrimary: true
        additionalSecondary: true
    }
}

declare module '@mui/material/Checkbox' {
    interface CheckboxPropsColorOverrides {
        additionalPrimary: true
        additionalSecondary: true
    }
}

declare module '@mui/material/Badge' {
    interface BadgePropsColorOverrides {
        additionalPrimary: true
        additionalSecondary: true
        onAdditionalPrimary: true
    }
}

declare module '@mui/material/Switch' {
    interface SwitchPropsSizeOverrides {
        large: true
    }
    interface SwitchClasses {
        sizeLarge: SwitchClasses["sizeMedium"]
    }
}

export function createTheme() {
    const fontFamily = [`'Open Sans'`, 'sans-serif'].join(",");
    const typography: {[key: string]: CSSObject} = {
        headerTitle: {
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: "25px",
            letterSpacing: "0.002em",
        },
        menuTitle: {
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: "25px",
            letterSpacing: "0.002em",
        },
        searchText: {
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em",
        },
        listText: {
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em",
        },
        statusText: {
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em",
        },
        cardMenuTitle: {
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "16px",
            letterSpacing: "0.002em"
        },
        cardMenuStatus: {
            fontSize: "8px",
            fontWeight: 700,
            lineHeight: "10px",
            letterSpacing: "0.002em",
        },
        screenTitle: {
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "25px",
            letterSpacing: "0.005em"
        },
        screenText: {
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "25px",
            letterSpacing: "0.005em"
        },
        screenSubtitle: {
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "20px",
            letterSpacing: "0.002em",
        },
        cardTitle1: {
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "25px",
            letterSpacing: "0.002em"
        },
        cardTitle2: {
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "20px",
            letterSpacing: "0.002em"
        },
        cardText: {
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em"
        },
        inputTitle1: {
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "20px",
            letterSpacing: "0.002em"
        },
        inputText1: {
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "25px",
            letterSpacing: "0.002em"
        },
        inputTitle2: {
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em"
        },
        inputText2: {
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em"
        },
        btnText1: {
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "25px",
            letterSpacing: "0.002em",
        },
        btnText2: {
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "20px",
            letterSpacing: "0.002em",
        },
        elementText: {
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "25px",
            letterSpacing: "0.002em",
        },
        tagsText: {
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "16px",
            letterSpacing: "0.002em",
        },
        countText: {
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "20px",
            letterSpacing: "0.002em"
        }
    }
    const colors = {
        inputColor: "#F7F7FA",
        inputColorDisabled: "#F5F5F7",
        inputColorPlaceholder: "#F0F4F9",
        mainPrimary: "#18395B",
        mainSecondary: "#6E798C",
        mainSecondaryDisabled: "#B6BBC5",
        additionalPrimary: "#3F79B5",
        additionalPrimaryDisabled: "#9EBBD9",
        additionalSecondary: "#B6BBC5",
        additionalSecondaryDisabled: "#DADDE2",
    }


    return createMuiTheme({
        components: {
            MuiTypography: {
                variants: Object.entries(typography).map(([variant, style]) => ({
                    props: {
                        variant: variant,
                    } as Partial<TypographyProps>,
                    style: style
                })),
                styleOverrides: {
                    root: {
                        color: "#232326"
                    }
                }
            },
            MuiTextField: {
                defaultProps: {
                    variant: "filled"
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        fontWeight: 600,
                        fontSize: 16,
                        color: colors.mainPrimary,
                        appearance: "none",
                        "&::placeholder": {
                            color: colors.mainSecondary,
                            opacity: 1,
                        },
                        "&.Mui-disabled": {
                            fontWeight: 400,
                        }
                    },
                }
            },
            MuiFilledInput: {
                defaultProps: {
                    disableUnderline: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        backgroundColor: colors.inputColorPlaceholder,
                        paddingRight: 0,
                        "&:hover": {
                            backgroundColor: colors.inputColor,
                        },
                        "&.Mui-disabled": {
                            backgroundColor: colors.inputColorDisabled,
                        },
                        "&.Mui-focused": {
                            backgroundColor: colors.inputColorPlaceholder,
                            outline: "1px solid #3F79B5",
                        },
                        "&.Mui-error": {
                            outline: "1px solid #E53328",
                        }
                    },
                    input: {
                        padding: 16,
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                        "&:-webkit-autofill": {
                            borderRadius: 10,
                        },
                        "&.Mui-disabled": {
                            color: colors.additionalSecondary,
                            WebkitTextFillColor: "currentColor",
                            fontWeight: 400,
                        },
                        "&::placeholder": {
                            fontWeight: 400,
                            color: colors.mainSecondary,
                        }
                    },
                    sizeSmall: {
                        borderRadius: 8,
                        "&:-webkit-autofill": {
                            borderRadius: 8,
                        }
                    },
                    inputSizeSmall: {
                        padding: "6px 8px",
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: "18px",
                        letterSpacing: "0.002em",
                        height: 18,
                    },
                    multiline: {
                        padding: 16,
                    },
                    inputMultiline: {
                        padding: 0,
                    },
                }
            },
            MuiInputLabel: {
                defaultProps: {
                    shrink: true,
                },
                styleOverrides: {
                    filled: {
                        ...typography["inputTitle1"],
                        transition: "none",
                        transform: "none",
                        position: "inherit",
                        marginBottom: 12,
                        color: colors.mainPrimary,
                        fontWeight: 600,
                        "&$shrink": {
                            transform: "none",
                        },
                        "&.Mui-disabled": {
                            color: colors.mainSecondaryDisabled,
                        },
                        "&.Mui-focused": {
                            color: colors.mainPrimary,

                        },
                    },
                }
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        ...typography["inputTitle1"],
                        color: colors.mainSecondary,
                        "&.Mui-disabled": {
                            color: colors.mainSecondary,
                        },
                        "&.Mui-error": {
                            color: colors.mainPrimary,
                        }
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    label: {
                        ...typography["inputTitle2"],
                        color: colors.mainSecondary,
                        "&.Mui-disabled": {
                            color: colors.mainSecondary,
                        },
                    }
                }
            },
            MuiAutocomplete: {
                defaultProps: {
                    popupIcon: <DropdownIcon />,
                },
                styleOverrides: {
                    inputRoot: {
                        "&[class*='MuiFilledInput-root']": {
                            paddingTop: 0,
                            paddingLeft: 0,
                        },
                        "&[class*='MuiFilledInput-root'] .MuiAutocomplete-input": {
                            padding: "16px"
                        }
                    },
                    option: {
                        fontWeight: 600,
                        fontSize: "16px",
                        lineHeight: "20px",
                        letterSpacing: "0.002em",
                        height: "44px",
                    },
                    paper: {
                        borderRadius: 8
                    },
                    popper: {
                         marginTop: "8px !important"
                    }
                }
            },
            MuiSelect: {
                defaultProps: {
                    IconComponent: DropdownIcon,
                },
                styleOverrides: {
                    select: {
                        display: "inline-flex",
                        alignItems: "center"
                    },
                }
            },
            MuiCheckbox: {
                defaultProps: {
                    icon: <CheckboxOff/>,
                    checkedIcon: <CheckboxOn/>,
                    color: "additionalPrimary",
                }
            },
            MuiButton: {
                defaultProps: {
                    variant: "contained"
                },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        borderRadius: 8,
                    },
                    sizeMedium: {
                        height: 48,
                        fontWeight: 700,
                        fontSize: 16,
                        lineHeight: "25px",
                        letterSpacing: "0.002em"
                    },
                    sizeForty: {
                        height: 40,
                        padding: "4px 8px 4px 8px",
                        ...typography["btnText2"],
                    },
                    sizeSmall: {
                        height: 32,
                        padding: "10px 8px 10px 8px",
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: "20px",
                        letterSpacing: "0.002em"
                    },
                    contained: {
                        boxShadow: "none",
                    },
                    containedPrimary: {
                        color: "#FFFFFF",
                        backgroundColor: "#18395B",
                        "&:disabled": {
                            color: "#FFFFFF80",
                            backgroundColor: "#8797A9"
                        },
                        "&:active": {
                            color: "#FFFFFF",
                            backgroundColor: "#194F87"
                        }
                    },
                    containedSecondary: {
                        color: "#FFFFFF",
                        backgroundColor: "#6E798C",
                        "&:disabled": {
                            color: "#FFFFFF80",
                            backgroundColor: "#B6BBC5"
                        },
                        "&:active": {
                            color: "#FFFFFF",
                            backgroundColor: "#566889"
                        }
                    },
                    containedAdditionalPrimary: {
                        color: "#FFFFFF",
                        backgroundColor: "#3F79B5",
                        "&:disabled": {
                            color: "#FFFFFF80",
                            backgroundColor: "#9EBBD9"
                        },
                        "&:active": {
                            color: "#FFFFFF",
                            backgroundColor: "#325A83"
                        }
                    },
                    textSizeSmall: {
                        height: 32,
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: "20px",
                        letterSpacing: "0.002em"
                    },
                    textSizeMini: {
                        height: 24,
                        fontWeight: 600,
                        fontSize: 14,
                        lineHeight: "20px",
                        letterSpacing: "0.002em"
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    sizeMini: {
                        width: 24,
                        height: 24,
                        "& > .MuiSvgIcon-root": {
                            fontSize: "20px",
                        }
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 8
                    }
                }
            },
            MuiCardHeader: {
                styleOverrides: {
                    root: {
                        padding: 10,
                    }
                }
            },
            MuiCardContent: {
                styleOverrides: {
                    root: {
                        padding: 10,
                        "&:last-child": {
                            padding: 10,
                        }
                    }
                }
            },
            MuiCardActionArea: {
                styleOverrides: {
                    root: {
                        fontFamily: fontFamily
                    }
                }
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        borderColor: "rgba(110, 121, 140, 0.07)"
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                    },
                    sizeMedium: {
                        fontSize: "16px",
                        fontWeight: 600,
                        lineHeight: "20px",
                        letterSpacing: "0.002em",
                    },
                }
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        fontWeight: 600,
                        fontSize: 16,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    colorDefault: {
                        backgroundColor: "#ffffff",
                        color: "#18395B"
                    },
                    colorTransparent: {
                        "& .MuiSvgIcon-root, & .MuiTypography-root": {
                            color: "white !important",
                        }
                    }
                }
            },
            MuiListItem: {
                styleOverrides: {
                    padding: {
                        padding: "4px 16px"
                    }
                }
            },
            MuiListItemText: {
                styleOverrides: {
                    primary: {
                        fontWeight: 600,
                        fontSize: "16px",
                        lineHeight: "20px",
                        letterSpacing: "0.002em"
                    }
                }
            },
            MuiContainer: {
                defaultProps: {
                    maxWidth: "sm"
                }
            },
            MuiAlert: {
                defaultProps: {
                    iconMapping: {

                    },
                },
                styleOverrides: {
                    root: {
                        ...typography["statusText"],
                        color: "#6E798C",
                        padding: 10,
                        borderRadius: 8,
                    },
                    message: {
                        padding: 0,
                        wordBreak: "break-word"
                    },
                    icon: {
                        padding: 0,
                        marginRight: 10,
                    },
                    action: {
                        paddingTop: 0,
                        paddingLeft: 10,
                    },
                    standardSuccess: {
                        backgroundColor: "#FFFFFF"
                    },
                    standardError: {
                        backgroundColor: "#FFFFFF"
                    },
                    filledInfo: {
                        backgroundColor: "#B6BBC5",
                        color: "#FFFFFF",
                    },
                }
            },
            MuiAlertTitle: {
                styleOverrides: {
                    root: {
                        ...typography["listText"],
                        color: "#18395B",
                        marginBottom: 4
                    },
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 8,
                        width: "100%"
                    },
                    paperFullScreen: {
                        borderRadius: 0,
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        padding: "8px 10px",
                        color: colors.mainPrimary,
                        ...typography["cardTitle1"],
                    }
                }
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        padding: 10,
                    }
                }
            },
            MuiDialogContentText: {
                defaultProps: {
                    // @ts-ignore missing in typing
                    component: "span"
                },
                styleOverrides: {
                    root: {
                        color: colors.mainPrimary,
                        ...typography["cardText"],

                    }
                }
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        "& > *": {
                            flexGrow: 1,
                            width: "100%",
                        }
                    }
                }
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        borderRadius: "8px !important",
                    }
                }
            },
            MuiAccordionSummary: {
                defaultProps: {
                    expandIcon: <LinaArrowRight/>
                },
                styleOverrides: {
                    root: {
                        padding: "0 10px",
                    },
                    content: {
                        minWidth: 0,
                        margin: "8px 0",
                    },
                    expandIconWrapper: {
                        "&.Mui-expanded": {
                            transform: "rotate(90deg)"
                        }
                    }

                }
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: {
                        padding: 10,
                        paddingTop: 0,
                    }
                }
            },
            MuiTabs: {},
            MuiTab: {
                styleOverrides: {
                    root: {
                        ...typography["elementText"],
                        textTransform: "unset",
                        color: colors.mainSecondary,
                    },
                }
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        width: 36,
                        height: 20,
                        padding: 0,
                        marginRight: 12,
                    },
                    switchBase: {
                        padding: 0,
                        margin: 2,
                        transitionDuration: '300ms',
                        "&.Mui-checked": {
                            transform: "translateX(16px)",
                            color: "#fff",
                            "& + .MuiSwitch-track": {
                                opacity: 1,
                                backgroundColor: colors.additionalPrimary,
                                borderColor: colors.additionalPrimary,
                            },
                            ".MuiSwitch-thumb": {
                                backgroundColor: '#fff',
                            },
                            "&.Mui-disabled": {
                                "& + .MuiSwitch-track": {
                                    opacity: 1,
                                    backgroundColor: colors.additionalPrimaryDisabled,
                                    borderColor: colors.additionalPrimaryDisabled,
                                },
                                ".MuiSwitch-thumb": {
                                    backgroundColor: '#fff',
                                }
                            }
                        },
                        "&.Mui-disabled": {
                            "& + .MuiSwitch-track": {
                                borderColor: colors.additionalPrimaryDisabled,
                                opacity: 1,
                            },
                            ".MuiSwitch-thumb": {
                                opacity: 1,
                                backgroundColor: colors.additionalPrimaryDisabled,
                            }
                        }
                    },
                    track: {
                        borderRadius: 22,
                        backgroundColor: "#fff",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: colors.mainSecondary,
                        transition: "all 0.2s ease",
                    },
                    thumb: {
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: colors.mainSecondary,
                        boxShadow: "none",
                        transition: "all 0.2s ease",
                    },
                    sizeSmall: {
                        width: 28,
                        height: 16,
                        "& .MuiSwitch-thumb": {
                            width: 12,
                            height: 12,
                        },
                        "& .MuiSwitch-switchBase": {
                            padding: 0,
                            margin: 2,
                            "&.Mui-checked": {
                                transform: "translateX(12px)",
                            }
                        }
                    },
                    sizeLarge: {
                        width: 44,
                        height: 24,
                        "& .MuiSwitch-thumb": {
                            width: 20,
                            height: 20,
                        },
                        "& .MuiSwitch-switchBase": {
                            padding: 0,
                            margin: 2,
                            "&.Mui-checked": {
                                transform: "translateX(20px)",
                            }
                        }
                    },
                }
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        marginLeft: 0,
                        fontSize: '12px',
                        lineHeight: '17px',
                        marginTop: '4px',
                        '&.Mui-error': {
                            color: '#E53328'
                        }
                    }
                }
            }
        },
        palette: {
            primary: {
                main: colors.mainPrimary,
                contrastText: "#FFFFFF"
            },
            secondary: {
                main: colors.mainSecondary,
                contrastText: "#FFFFFF"
            },
            text: {
                primary: "#18395B",
                secondary: "#B6BBC5",
            },
            additionalPrimary: {
                main: "#3F79B5",
                contrastText: "#ffffff",
            },
            additionalPrimaryDisabled: {
                main: colors.additionalPrimaryDisabled,
                contrastText: "#FFFFFF",
            },
            onAdditionalPrimary: {
                main: "#FFFFFF",
                contrastText: "#3F79B5",
            },
            additionalSecondary: {
                main: "#B6BBC5",
                contrastText: "#FFFFFF"
            },
            background: {
                default: "#F7F7FA"
            },
            action: {
                disabledBackground: colors.inputColorDisabled,
                disabled: colors.additionalSecondary
            }
        },
        typography: {
            fontFamily: fontFamily,
            allVariants: {
                lineHeight: 1
            }
        },
        shadows: Array.from({length: 25}).map((_, index) => {
            return `0px ${index}px 24px rgba(104, 117, 130, 0.09)`;
        }) as Shadows,
    });
}


export type PortTheme = ReturnType<typeof createTheme>;
